import {
    type OryCardSettingsSectionProps,
    type OryFlowComponentOverrides,
    type OryFormRootProps,
} from '@ory/elements-react';
import {
    type FormEvent,
    type FormEventHandler,
    type MouseEvent,
    useState,
} from 'react';

import {
    getPasswordPolicyError,
    PASSWORD_POLICY_ERROR,
    PASSWORD_POLICY_PATTERN,
    shouldEnforcePasswordPolicy,
} from '@apps/web/components/auth/password_policy';

function CardRoot({ children }: React.PropsWithChildren) {
    return (
        <div className="flex w-full flex-1 items-start justify-center font-sans sm:w-[480px] sm:max-w-[480px] sm:items-center">
            <div className="sm:rounded-cards border-form-border-default bg-form-background-default relative grid w-full grid-cols-1 gap-8 border-b px-8 py-12 sm:border sm:px-12 sm:py-14">
                {children}
            </div>
        </div>
    );
}

type FormAction = React.ComponentPropsWithoutRef<'form'>['action'];

function PasswordPolicyForm({
    children,
    onSubmit,
    action,
    method,
    className,
    'data-testid': dataTestId,
}: React.PropsWithChildren<{
    readonly action?: FormAction;
    readonly className: string;
    readonly method?: string;
    readonly onSubmit: FormEventHandler<HTMLFormElement>;
    readonly 'data-testid'?: string;
}>) {
    const [passwordPolicyError, setPasswordPolicyError] = useState<
        string | null
    >(null);

    function validatePasswordPolicy(form: HTMLFormElement): boolean {
        const actionUrl = typeof action === 'string' ? action : undefined;

        if (shouldEnforcePasswordPolicy(actionUrl)) {
            const passwordInput = form.elements.namedItem('password');

            if (passwordInput instanceof HTMLInputElement) {
                const error = getPasswordPolicyError(passwordInput.value);

                if (error) {
                    setPasswordPolicyError(error);
                    passwordInput.focus();
                    return false;
                }
            }
        }

        setPasswordPolicyError(null);
        return true;
    }

    function handleClickCapture(event: MouseEvent<HTMLFormElement>) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const submitControl = event.target.closest('button, input');
        if (
            !(submitControl instanceof HTMLButtonElement) &&
            !(submitControl instanceof HTMLInputElement)
        ) {
            return;
        }

        if (submitControl.type !== 'submit' && submitControl.type !== 'image') {
            return;
        }

        if (!validatePasswordPolicy(event.currentTarget)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        if (!validatePasswordPolicy(event.currentTarget)) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        onSubmit(event);
    }

    function configurePasswordInput(form: HTMLFormElement | null) {
        const actionUrl = typeof action === 'string' ? action : undefined;
        if (!form || !shouldEnforcePasswordPolicy(actionUrl)) {
            return;
        }

        const passwordInput = form.elements.namedItem('password');
        if (!(passwordInput instanceof HTMLInputElement)) {
            return;
        }

        passwordInput.minLength = 8;
        passwordInput.maxLength = 32;
        passwordInput.pattern = PASSWORD_POLICY_PATTERN;
        passwordInput.title = PASSWORD_POLICY_ERROR;
    }

    return (
        <form
            ref={configurePasswordInput}
            data-testid={dataTestId}
            onClickCapture={handleClickCapture}
            onSubmit={handleSubmit}
            noValidate={true}
            action={action}
            method={method}
            className={className}
        >
            {passwordPolicyError && (
                <p
                    role="alert"
                    className="text-interface-foreground-validation-danger text-sm leading-normal"
                >
                    {passwordPolicyError}
                </p>
            )}
            {children}
        </form>
    );
}

function FormRoot(props: React.PropsWithChildren<OryFormRootProps>) {
    return <PasswordPolicyForm {...props} className="grid gap-8" />;
}

function SettingsSection({
    children,
    action,
    method,
    onSubmit,
}: React.PropsWithChildren<OryCardSettingsSectionProps>) {
    return (
        <PasswordPolicyForm
            action={action}
            method={method}
            onSubmit={onSubmit}
            className="flex w-full max-w-screen-sm flex-col px-4 md:max-w-[712px] lg:max-w-[802px] xl:max-w-[896px]"
        >
            {children}
        </PasswordPolicyForm>
    );
}

export const oryComponentOverrides: OryFlowComponentOverrides = {
    Card: {
        Root: CardRoot,
        SettingsSection,
    },
    Form: {
        Root: FormRoot,
    },
};
